import {useGetDrugCatalogItem} from '@/modules/drug-catalog/substances/api'
import {useGetPatientTherapySequenceActivities} from '@/modules/patients-therapies/api/use-get-patient-therapy-sequence-activities'
import {useGetPrescribedName} from '@/modules/products/trade-names/api'
import {GroupType, JsonPatchOp} from '@cypro/engine/database/schema'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {EventLogResources, isPartOfGroup} from '@cypro/engine/utils'
import {useMemo} from 'react'
import {
  type DoctorChoiceValues,
  type DoctorPrescribedDisplay,
  formatDoctorPrescribed,
  resolveDoctorChoice,
} from './doctor-prescribed-values'

/**
 * What the doctor released for each medicine of a prescription: the trade name, or the
 * substance and the dose as it was validated. The doctor-validation snapshot wins; rows
 * validated before it existed fall back to the doctor's prescribing log.
 */
export function useDoctorPrescribedMedicines(sequence: PatientTherapySequence | undefined): DoctorPrescribedDisplay[] {
  const medicines = sequence?.medicine ?? []
  const medicineIds = medicines.map(medicine => medicine.id).filter(Boolean)
  const [logs] = useGetPatientTherapySequenceActivities(sequence?.id, medicineIds)

  const doctorLogs = medicines.map(medicine => findDoctorLog(logs, medicine.id))
  const choices = medicines.map((medicine, index) =>
    resolveDoctorChoice(medicine, doctorLogs[index] ? readLoggedValues(doctorLogs[index].patches) : {})
  )

  // the doctor's product and substance are resolved by id, so a later pharmacist change does not swap them
  const [doctorPrescribedName1] = useGetPrescribedName(choices[0]?.prescribedNameId)
  const [doctorPrescribedName2] = useGetPrescribedName(choices[1]?.prescribedNameId)
  const [doctorSubstance1] = useGetDrugCatalogItem(choices[0]?.activeSubstanceId)
  const [doctorSubstance2] = useGetDrugCatalogItem(choices[1]?.activeSubstanceId)
  const doctorPrescribedNames = [doctorPrescribedName1, doctorPrescribedName2]
  const doctorSubstances = [doctorSubstance1, doctorSubstance2]

  return useMemo(() => {
    return medicines.map((_medicine, index) => {
      const substance = doctorSubstances[index]
      return formatDoctorPrescribed(choices[index], {
        tradeName: doctorPrescribedNames[index],
        substance: {
          name: substance?.name,
          doseUnit: substance?.activeSubstanceDetails?.doseUnit?.ucumCode ?? '',
        },
      })
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sequence, logs, doctorPrescribedName1, doctorPrescribedName2, doctorSubstance1, doctorSubstance2])
}

/** the last medicine log a doctor wrote that carries prescribing values (a doctor-validation snapshot log carries none) */
function findDoctorLog(
  logs: Awaited<ReturnType<typeof useGetPatientTherapySequenceActivities>>[0],
  medicineId: string | undefined
) {
  return (logs ?? []).find(
    log =>
      log.resource === EventLogResources.patientTherapySequenceMedicine &&
      log.domain === 'prescription' &&
      log.resourceId === medicineId &&
      isPartOfGroup(log.actor?.groups, GroupType.enum.DOCTOR) &&
      hasPrescribingValues(readLoggedValues(log.patches))
  )
}

function hasPrescribingValues(values: DoctorChoiceValues) {
  return Boolean(values.prescribedNameId || values.activeSubstanceId || values.totalAmount)
}

/** picks the payload out of a prescribing log's RFC-6902 diff (create = full row, update = changes) */
function readLoggedValues(patches: JsonPatchOp[] | null | undefined): DoctorChoiceValues {
  const values: DoctorChoiceValues = {}
  const _patches = Array.isArray(patches) ? patches : []

  for (const patch of _patches) {
    if (patch.path === '' && patch.op === 'add') {
      const full = patch.value as DoctorChoiceValues
      values.prescribedNameId = full?.prescribedNameId || undefined
      values.activeSubstanceId = full?.activeSubstanceId || undefined
      values.totalAmount = full?.totalAmount ?? undefined
    }

    if ('value' in patch) {
      if (patch.path === '/prescribedNameId' && typeof patch.value === 'string') {
        values.prescribedNameId = patch.value
      }
      if (patch.path === '/activeSubstanceId' && typeof patch.value === 'string') {
        values.activeSubstanceId = patch.value
      }
      if (patch.path === '/totalAmount' && typeof patch.value === 'number') {
        values.totalAmount = patch.value
      }
    }
  }

  return values
}
